import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  ChartBarIcon, 
  UserGroupIcon, 
  BookOpenIcon,
  SparklesIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  MoonIcon,
  SunIcon
} from '@heroicons/react/24/outline';

// Theme hook
function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 
        (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    }
    return 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  return [theme, setTheme];
}

function Landing() {
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();

  const features = [
    {
      icon: AcademicCapIcon,
      title: 'GPA Calculator',
      description: 'Track your different GPA\'s with DVHS-compliant calculations. See graduation progress instantly.'
    },
    {
      icon: BookOpenIcon,
      title: 'Course Planner',
      description: 'Plan your academic future with our comprehensive course catalog. Filter by department, graduation requirements, and course type.'
    },
    {
      icon: ChartBarIcon,
      title: 'What-If Analysis',
      description: 'Simulate future grades and see how they impact your GPA. Plan ahead with confidence and make informed decisions.'
    },
    {
      icon: UserGroupIcon,
      title: 'Clubs & Teachers',
      description: 'Explore 100+ DVHS clubs and browse teacher information. Find your community and connect with them.'
    },
    {
      icon: DocumentTextIcon,
      title: 'Transcript Upload',
      description: 'Upload your PDF transcript and let our OCR technology automatically parse your academic history.'
    },
    {
      icon: SparklesIcon,
      title: 'Beautiful UI',
      description: 'Modern, responsive design with dark mode support and customizable accent colors. Built for students, by students.'
    }
  ];

  const stats = [
    { label: 'Course Catalog', value: '100+' },
    { label: 'DVHS Clubs', value: '100+' },
    { label: 'Teacher Directory', value: '100+' },
    { label: 'GPA Types', value: '4+' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Dark Mode Toggle */}
      <button
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        className="fixed top-4 right-4 p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-50 border border-gray-200 dark:border-gray-700"
        aria-label="Toggle dark mode"
      >
        {theme === 'dark' ? (
          <SunIcon className="h-5 w-5 text-yellow-400" />
        ) : (
          <MoonIcon className="h-5 w-5 text-gray-700" />
        )}
      </button>

      {/* Hero Section */}
      <div className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 sm:pt-24 sm:pb-20">
          <div className="text-center">
            {/* Logo/Brand */}
            <div className="flex items-center justify-center mb-8">
              <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800">
                <AcademicCapIcon className="h-12 w-12 text-[var(--accent-600)]" />
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              Welcome to DVTime.
            </h1>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Your all-in-one academic companion for Dougherty Valley High School.
            </h1>
            
            <p className="text-base text-gray-500 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
              Track your GPA, plan courses, explore clubs, and visualize your academic journey with powerful what-if scenarios.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/register')}
                className="group px-6 py-3 bg-[var(--accent-600)] hover:bg-[var(--accent-700)] text-white font-semibold rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 flex items-center gap-2"
              >
                Get Started
                <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-semibold rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700"
              >
                Sign In
              </button>
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-lg dark:hover:shadow-gray-900/50 transition-all duration-200">
                  <div className="text-2xl sm:text-3xl font-bold text-[var(--accent-600)] mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Everything You Need to Succeed
            </h2>
            <p className="text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              DVTime combines powerful academic tools with an intuitive interface to help you reach your goals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-950 rounded-xl p-6 border border-gray-200 dark:border-gray-800 hover:border-[var(--accent-600)] dark:hover:border-[var(--accent-600)] transition-all duration-200 hover:shadow-lg dark:hover:shadow-gray-900/50"
              >
                <div className="bg-white dark:bg-gray-900 w-12 h-12 rounded-lg flex items-center justify-center mb-4 border border-gray-200 dark:border-gray-800">
                  <feature.icon className="h-6 w-6 text-[var(--accent-600)]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Get Started in Minutes
            </h2>
            <p className="text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Three simple steps to unlock your academic potential.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-[var(--accent-600)] text-white w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-sm">
                1
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Create Account
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Sign up with your email.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-[var(--accent-600)] text-white w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-sm">
                2
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Upload Transcript
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Upload your PDF transcript and our AI will parse your courses and grades automatically.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-[var(--accent-600)] text-white w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 shadow-sm">
                3
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Start Planning
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Explore courses, track your GPA, and plan your academic future with confidence.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="py-16 bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Take Control of Your Academic Journey?
          </h2>
          <p className="text-base text-gray-600 dark:text-gray-400 mb-8">
            Join DVHS students already using DVTime to plan their future.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="group px-8 py-3 bg-[var(--accent-600)] hover:bg-[var(--accent-700)] text-white font-semibold rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 inline-flex items-center gap-2"
          >
            Get Started Free
            <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white dark:bg-gray-900 py-8 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-500 dark:text-gray-400">
            <p className="text-sm mb-1">© 2025 DVTime. All rights reserved.</p>
            <p className="text-xs">Made with ❤️ by students.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Landing;
